import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { showsAPI, cuesAPI, vmixAPI, singularAPI, vmixInstancesAPI } from '../lib/api';
import { useShowStore } from '../stores/showStore';
import { useConnectionStore } from '../stores/connectionStore';
import { useThemeStore } from '../stores/themeStore';
import { useSettingsStore } from '../stores/settingsStore';
import { computeInTimes } from '../lib/rundownDerive';
import { useWebSocket } from '../hooks/useWebSocket';
import { useEditLock } from '../hooks/useEditLock';
import { LOCAL_FIRST } from '../lib/powersync/db';
import { watchCues, watchShow } from '../lib/powersync/rundownLocal';
import { isDesktop, agent, connectAgentEvents } from '../lib/desktop';

import TopBar from '../components/layout/TopBar';
import ShowSettings from '../components/layout/ShowSettings';
import SubHeaderModern from '../components/layout/SubHeaderModern';
import RundownTable from '../components/rundown/RundownTable';
import ProducerView from '../components/views/ProducerView';
import ProducerViewModern from '../components/views/ProducerViewModern';
import TDView from '../components/views/TDView';
import GraphicsView from '../components/views/GraphicsView';
import SoloView from '../components/views/SoloView';
import RawView from '../components/views/RawView';
import SingularView from '../components/views/SingularView';

const VIEWS = ['producer', 'td', 'graphics', 'commentary', 'raw', 'singular'];

// Minimum gap between takes when the double-tap guard is on.
const TAKE_GUARD_MS = 500;

export default function RundownEditor() {
  const { id: showId } = useParams();
  const navigate = useNavigate();

  // Reactive state only — actions are read from getState() at call time
  const show = useShowStore(s => s.show);
  const onAirCue = useShowStore(s => s.cues.find(c => c.status === 'on_air') || null);
  const nextCue = useShowStore(s => {
    const idx = s.cues.findIndex(c => c.status === 'on_air');
    return s.cues.slice(idx + 1).find(c => c.status === 'pending') || s.cues.find(c => c.status === 'pending') || null;
  });

  // Sequential operation queue — ensures API calls reach the backend in order
  // even when the user presses buttons faster than the network round-trip.
  const opQueueRef = useRef(Promise.resolve());
  const reconcileTimerRef = useRef(null);

  const theme = useThemeStore(s => s.theme);
  const uiStyle = useSettingsStore(s => s.uiStyle);
  const editLock = useSettingsStore(s => s.editLock);
  useEditLock(editLock);

  const [activeView, setActiveView] = useState('producer');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [autoMode, setAutoMode] = useState(false);
  const autoModeRef = useRef(false);
  // Transient warning under the chrome. Not an error: the page keeps working,
  // it just says why the thing that was clicked did not happen.
  const [notice, setNotice] = useState('');
  const noticeTimerRef = useRef(null);

  function flashNotice(message) {
    setNotice(message);
    clearTimeout(noticeTimerRef.current);
    noticeTimerRef.current = setTimeout(() => setNotice(''), 4000);
  }
  useEffect(() => () => clearTimeout(noticeTimerRef.current), []);

  // Auto mode only means anything on a live show: the take loop below refuses to
  // fire otherwise, so an AUTO pill lit on a draft show was a green light for
  // something that would never happen. Refuse the toggle and say why instead.
  function toggleAutoMode() {
    if (autoMode) { setAutoMode(false); return; }
    if (useShowStore.getState().show?.status !== 'live') {
      flashNotice('Auto mode needs the show to be live. Hit GO LIVE first.');
      return;
    }
    setAutoMode(true);
  }

  // Ending or resetting a show has to take AUTO down with it, or the pill keeps
  // claiming cues are advancing on their own after the loop has stopped firing.
  useEffect(() => {
    if (autoMode && show?.status !== 'live') {
      setAutoMode(false);
      flashNotice('Auto mode switched off — the show is no longer live.');
    }
  }, [show?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  // Double-tap guard: when on, a second TAKE within TAKE_GUARD_MS is ignored so
  // an accidental double-tap (or held space) doesn't skip a cue. Persisted so it
  // survives a mid-show reload. Off by default — fly freely while building.
  const [takeGuard, setTakeGuard] = useState(() => {
    try { return localStorage.getItem('mc-take-guard') === '1'; } catch { return false; }
  });
  const takeGuardRef = useRef(takeGuard);
  const lastTakeAtRef = useRef(0);
  useEffect(() => { takeGuardRef.current = takeGuard; }, [takeGuard]);
  const toggleTakeGuard = () => setTakeGuard(v => {
    const next = !v;
    try { localStorage.setItem('mc-take-guard', next ? '1' : '0'); } catch { /* ignore */ }
    return next;
  });

  const { sendMessage } = useWebSocket(showId);

  // Load show data
  useEffect(() => {
    async function loadShow() {
      try {
        setLoading(true);
        const res = await showsAPI.get(showId);
        useShowStore.getState().setShow(res.data);
        useShowStore.getState().setCues(res.data.cues || []);
        setError('');
        // Workspace vMix instances a cue action can target. Loaded into the
        // connection store for the action editor, and handed to the local agent
        // below so it can address a non-Primary instance on the LAN.
        const instances = await vmixInstancesAPI.list()
          .then(r => r.data)
          .catch(() => []);
        useConnectionStore.getState().setVmixInstances(instances);
        // Desktop: hand the local agent this show's vMix context + cue set so
        // it can drive vMix on the LAN and resolve loop-control targets.
        if (isDesktop) {
          agent.setSession(showId, {
            vmix: res.data.vmix_host ? { host: res.data.vmix_host, port: res.data.vmix_port } : undefined,
            instances,
            cues: useShowStore.getState().cues,
          }).catch(() => {});
        }
        if (res.data.vmix_host) {
          vmixAPI.connect(showId).catch(() => {});
        }
        // Singular: ask the SERVER whether a token is stored. It cannot be read
        // off the show row — the key is redacted from REST and absent from the
        // local-first sync rules — so gating on res.data.singular_api_key meant
        // that after every refresh the editor decided Singular was unconfigured
        // and skipped this fetch, leaving the cue-action composition dropdowns
        // empty until the token was re-entered by hand.
        singularAPI.status(showId)
          .then(({ data }) => {
            useConnectionStore.getState().setSingularStatus(data);
            if (!data.connected) return;
            return singularAPI.compositions(showId)
              .then(r => useConnectionStore.getState().setSingularCompositions(r.data));
          })
          .catch(() => useConnectionStore.getState().setSingularStatus({ configured: false, connected: false }));
      } catch (err) {
        if (err.response?.status === 404) {
          navigate('/');
        } else {
          setError(err.response?.data?.error || 'Failed to load show');
        }
      } finally {
        setLoading(false);
      }
    }

    loadShow();
    return () => useShowStore.getState().resetStore();
  }, [showId, navigate]);

  // Local-first: keep cue CONTENT + STRUCTURE live from the local store via
  // PowerSync's reactive watch. Live-EXECUTION state (status + timing) is owned
  // by the WS channel here, so preserve it over the (possibly lagging) synced
  // value when reconciling.
  useEffect(() => {
    if (!LOCAL_FIRST) return;
    const unsub = watchCues(showId, (incoming) => {
      const prev = useShowStore.getState().cues;
      const byId = new Map(prev.map(c => [c.id, c]));
      const merged = incoming.map(c => {
        const cur = byId.get(c.id);
        return cur ? { ...c, status: cur.status, actual_duration: cur.actual_duration } : c;
      });
      useShowStore.getState().setCues(merged);
      // Keep the agent's cue snapshot current for loop-control targets.
      if (isDesktop) agent.setSession(showId, { cues: merged }).catch(() => {});
    });
    return () => unsub();
  }, [showId]);

  // Local-first: keep the show's OWN row (start time, name, status, …) live too.
  // Without this the show was read once on load, so a start-time change made on
  // another machine only showed up there after a manual refresh. Overlay the
  // synced columns onto the current show object.
  useEffect(() => {
    if (!LOCAL_FIRST) return;
    const unsub = watchShow(showId, (row) => {
      if (!row) return;
      const prev = useShowStore.getState().show;
      if (!prev || prev.id !== row.id) return;
      useShowStore.getState().setShow({ ...prev, ...row });
    });
    return () => unsub();
  }, [showId]);

  // Desktop: subscribe to the local agent's event stream (vMix tally + Singular
  // status) instead of the cloud WebSocket's vMix events.
  useEffect(() => {
    if (!isDesktop) return;
    const unsub = connectAgentEvents({
      onTally: (t) => useConnectionStore.getState().setVmixTally({ program: t.program || [], preview: t.preview || [] }),
      onSingularStatus: () => {},
      onActionsStart: (d) => d?.cueId && useShowStore.getState().setCueActionsRunning(d.cueId, true),
      onActionsEnd: (d) => d?.cueId && useShowStore.getState().setCueActionsRunning(d.cueId, false),
    });
    return () => unsub();
  }, [showId]);

  // Keyboard shortcuts
  useEffect(() => {
    function handleKeyDown(e) {
      // Skip if typing in an input
      const tag = e.target.tagName.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target.isContentEditable) {
        if (e.key === 'Escape') e.target.blur();
        return;
      }

      switch (e.key) {
        case ' ':
          e.preventDefault();
          handleTake();
          break;
        case 'b':
        case 'B':
          handleBack();
          break;
        case 's':
        case 'S':
          handleSkip();
          break;
        case 'r':
        case 'R':
          useShowStore.getState().resetSegmentTimer();
          break;
        case '?':
          setShowHelp(h => !h);
          break;
        case '1':
          setActiveView('producer');
          break;
        case '2':
          setActiveView('td');
          break;
        case '3':
          setActiveView('graphics');
          break;
        case '4':
          setActiveView('commentary');
          break;
        case '5':
          setActiveView('raw');
          break;
        case '6':
          setActiveView('singular');
          break;
        case 'Escape':
          setShowHelp(false);
          break;
        default:
          break;
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // Handlers read live from store — no closure deps needed
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Enqueue an operation: applies an optimistic update immediately, then runs
  // the API call after all previous queued calls have completed. When the queue
  // drains, schedules a single reconcile fetch to pick up any missed WS events
  // (e.g. changes from other users that were blocked while ops were in-flight).
  function enqueueOp({ optimistic, revert, apiFn }) {
    if (optimistic) optimistic();
    useShowStore.getState().incrementPendingOps();

    opQueueRef.current = opQueueRef.current
      .then(() => apiFn())
      .catch(err => {
        console.error('Op failed:', err.message);
        if (revert) revert();
      })
      .finally(() => {
        useShowStore.getState().decrementPendingOps();
        // Debounced reconcile — fires 600ms after the LAST op in a burst settles
        if (reconcileTimerRef.current) clearTimeout(reconcileTimerRef.current);
        reconcileTimerRef.current = setTimeout(async () => {
          if (useShowStore.getState().pendingOpCount !== 0) return;
          try {
            const res = await cuesAPI.list(showId);
            if (useShowStore.getState().pendingOpCount !== 0) return;
            useShowStore.getState().setCues(res.data);
          } catch {}
        }, 600);
      });
  }

  function handleTake() {
    // Double-tap guard (when enabled): swallow a TAKE that lands too soon after
    // the last one. Checked before anything else so both space and the button
    // are covered; the timestamp is recorded only when a real take proceeds.
    if (takeGuardRef.current && Date.now() - lastTakeAtRef.current < TAKE_GUARD_MS) return;

    const s = useShowStore.getState();
    if (s.show?.status === 'complete') return;

    const currentOnAir = s.cues.find(c => c.status === 'on_air') || null;
    const next = s.cues
      .filter(c => c.status === 'pending' && (!currentOnAir || c.order > currentOnAir.order))
      .sort((a, b) => a.order - b.order)[0] || null;

    if (!next) return;

    lastTakeAtRef.current = Date.now();

    enqueueOp({
      optimistic: () => {
        const { updateCueStatus } = useShowStore.getState();
        if (currentOnAir) updateCueStatus(currentOnAir.id, 'complete');
        updateCueStatus(next.id, 'on_air');
      },
      revert: () => {
        const { updateCueStatus } = useShowStore.getState();
        updateCueStatus(next.id, 'pending');
        if (currentOnAir) updateCueStatus(currentOnAir.id, 'on_air');
      },
      apiFn: () => cuesAPI.take(showId),
    });
  }

  function handleSkip() {
    const s = useShowStore.getState();
    const currentOnAir = s.cues.find(c => c.status === 'on_air') || null;
    const next = s.cues
      .filter(c => c.status === 'pending' && (!currentOnAir || c.order > currentOnAir.order))
      .sort((a, b) => a.order - b.order)[0] || null;

    if (!next) return;

    enqueueOp({
      optimistic: () => useShowStore.getState().updateCueStatus(next.id, 'complete'),
      revert: () => useShowStore.getState().updateCueStatus(next.id, 'pending'),
      apiFn: () => cuesAPI.skip(showId),
    });
  }

  // Keep autoModeRef in sync for use inside interval
  React.useEffect(() => { autoModeRef.current = autoMode; }, [autoMode]);

  // Auto mode: compute next cue's scheduled in-time and fire TAKE when reached
  React.useEffect(() => {
    if (!autoMode) return;
    const interval = setInterval(() => {
      if (!autoModeRef.current) return;
      const s = useShowStore.getState();
      if (s.show?.status !== 'live') return;

      // Scheduled in-times — shared derivation (returns nulls when no start set,
      // in which case nextInTime is null below and no auto-take fires).
      const cues = s.cues;
      const inTimes = computeInTimes(cues, s.show);

      const onAirCue = cues.find(c => c.status === 'on_air') || null;
      const nextCue = onAirCue
        ? cues.slice(cues.findIndex(c => c.id === onAirCue.id) + 1).find(c => c.status === 'pending')
        : cues.find(c => c.status === 'pending');

      if (!nextCue) return;

      const nextIdx = cues.findIndex(c => c.id === nextCue.id);
      const nextInTime = inTimes[nextIdx];
      if (!nextInTime) return;

      const now = new Date();
      if (now >= nextInTime) {
        handleTake();
      }
    }, 500);
    return () => clearInterval(interval);
  }, [autoMode]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleBack() {
    const s = useShowStore.getState();
    const currentOnAir = s.cues.find(c => c.status === 'on_air') || null;
    if (!currentOnAir) return;

    // Find the immediately previous cue (any complete/skipped status, highest order below on_air)
    const prev = [...s.cues]
      .reverse()
      .find(c => c.status === 'complete' && c.order < currentOnAir.order) || null;

    if (!prev) return;

    // Queue like TAKE/SKIP — no automation ever fires for BACK
    enqueueOp({
      optimistic: () => {
        const { updateCueStatus } = useShowStore.getState();
        updateCueStatus(currentOnAir.id, 'pending');
        updateCueStatus(prev.id, 'on_air');
      },
      revert: () => {
        const { updateCueStatus } = useShowStore.getState();
        updateCueStatus(currentOnAir.id, 'on_air');
        updateCueStatus(prev.id, 'complete');
      },
      apiFn: () => cuesAPI.back(showId),
    });
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-app-bg flex items-center justify-center">
        <div className="text-gray-500">Loading show...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-app-bg flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-400 mb-4">{error}</div>
          <button onClick={() => navigate('/')} className="text-blue-500 hover:text-blue-400 text-sm">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!show) return null;

  // Modern producer = the timeline spine + its own single bar of chrome. Every
  // other combination keeps the standard Header + SubHeader + StatusBar so the
  // non-producer tabs are unchanged.
  const modernProducer = uiStyle === 'modern' && activeView === 'producer';

  return (
    <div className={`${theme === 'light' ? 'theme-light' : 'theme-dark'} style-${uiStyle} min-h-screen bg-app-canvas flex flex-col`} style={{ height: '100vh', overflow: 'hidden' }}>
      {modernProducer ? (
        <SubHeaderModern
          show={show}
          showId={showId}
          activeView={activeView}
          onViewChange={setActiveView}
          onExit={() => navigate('/')}
          onOpenSettings={() => setShowSettings(true)}
          onTake={handleTake}
          onSkip={handleSkip}
          onBack={handleBack}
          onReset={() => useShowStore.getState().resetAllCues()}
          canTake={!!nextCue && show.status !== 'complete'}
          autoMode={autoMode}
          onToggleAutoMode={toggleAutoMode}
          takeGuard={takeGuard}
          onToggleTakeGuard={toggleTakeGuard}
        />
      ) : (
        <TopBar
          show={show}
          showId={showId}
          activeView={activeView}
          onViewChange={setActiveView}
          onExit={() => navigate('/')}
          onOpenSettings={() => setShowSettings(true)}
          onTake={handleTake}
          onSkip={handleSkip}
          onBack={handleBack}
          onReset={() => useShowStore.getState().resetAllCues()}
          canTake={!!nextCue && show.status !== 'complete'}
          autoMode={autoMode}
          onToggleAutoMode={toggleAutoMode}
          takeGuard={takeGuard}
          onToggleTakeGuard={toggleTakeGuard}
        />
      )}

      {/* Says why clicks do nothing, so a locked page is never mistaken for a frozen one. */}
      {editLock && (
        <div className="px-4 py-1.5 bg-amber-900/25 border-b border-amber-800/50 text-amber-400 text-xs flex items-center gap-2">
          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <rect x="5" y="11" width="14" height="10" rx="2" />
            <path d="M8 11V7a4 4 0 0 1 8 0v4" />
          </svg>
          <span>Locked — only TAKE, SKIP and BACK work (buttons, or Space / S / B). Unlock with the padlock, top right.</span>
        </div>
      )}

      {notice && (
        <div className="px-4 py-2 bg-amber-900/25 border-b border-amber-800/50 text-amber-400 text-xs flex items-center gap-2">
          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
          <span>{notice}</span>
          <button onClick={() => setNotice('')} className="ml-auto text-amber-600 hover:text-amber-300" title="Dismiss">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      <div className="flex-1 overflow-hidden">
        {activeView === 'producer' && (
          uiStyle === 'modern'
            ? <ProducerViewModern showId={showId} show={show} />
            : <ProducerView showId={showId} show={show} />
        )}
        {activeView === 'td' && (
          <TDView showId={showId} show={show} />
        )}
        {activeView === 'graphics' && (
          <GraphicsView showId={showId} show={show} />
        )}
        {activeView === 'commentary' && (
          <SoloView showId={showId} show={show} />
        )}
        {activeView === 'raw' && (
          <RawView showId={showId} show={show} />
        )}
        {activeView === 'singular' && (
          <SingularView showId={showId} />
        )}
      </div>

      {showSettings && (
        <ShowSettings
          show={show}
          showId={showId}
          onClose={() => setShowSettings(false)}
        />
      )}

      {/* Help Modal */}
      {showHelp && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50"
          onClick={() => setShowHelp(false)}
        >
          <div
            className="bg-app-panel border border-app-border rounded-lg p-6 w-80"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="font-medium text-app-fg mb-4">Keyboard Shortcuts</h3>
            <div className="space-y-2 text-sm">
              {[
                ['Space', 'TAKE — advance rundown'],
                ['B', 'BACK — go back one cue'],
                ['S', 'SKIP — skip current cue'],
                ['N', 'New cue (in producer view)'],
                ['Del', 'Delete selected cues (in producer view)'],
                ['1', 'Switch to Producer view'],
                ['2', 'Switch to TD view'],
                ['3', 'Switch to Graphics view'],
                ['4', 'Switch to Commentary view'],
                ['5', 'Switch to Raw view'],
                ['6', 'Switch to Singular view'],
                ['?', 'Toggle this help'],
                ['Esc', 'Close / cancel edit'],
              ].map(([key, desc]) => (
                <div key={key} className="flex items-center justify-between">
                  <kbd className="px-1.5 py-0.5 bg-app-surface border border-app-border-strong rounded text-xs text-gray-300 font-mono min-w-[2.5rem] text-center">
                    {key}
                  </kbd>
                  <span className="text-gray-400 ml-3 flex-1">{desc}</span>
                </div>
              ))}
            </div>
            <button
              onClick={() => setShowHelp(false)}
              className="mt-5 w-full py-2 bg-app-surface hover:bg-app-hover text-gray-300 text-sm rounded transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
